
<template>
  <Page>
    <div class="page placemaking">
      <Title
        title="Placemaking the Campus"
        shape="rounner"
        subtitle="Adaptable tools for building a schoolscape."
      ></Title>
      <div class="content">
        <div class="section">
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="toc green toc--is-shape">
            <div class="toc-header">
              <div class="toc-header-svg">
                <Shape shape="rounner"></Shape>
              </div>
            </div>
            <div class="toc-content">
              <p>
                Placemaking the Campus Complex is about walking the path from
                fiction to agreement. The first Learner Card had spaces that
                hadn’t agreed to be part of our schoolscape yet. It was a
                fictional artifact that asked: if this map were true, what
                possibilities could it afford? Here are tools we created to
                bring the Campus from fiction into the built environment.
              </p>
            </div>
            <div class="toc-border"></div>
          </div>
          <div class="toc-feet green">
            <div class="toc-foot-1"></div>
            <div class="toc-foot-2"></div>
          </div>
          <div class="full-width-line">
            <div class="full-width-gradient"></div>
          </div>
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="spacer"></div>

          <!-- <Header first="Tools for Traversing"></Header> -->
          <div class="tool">
            <span id="learner-card"></span>
            <p>
              <b>Learner Card</b> is a map of the Campus Complex and a passport
              of sorts that gives Fellows access to the learning environments
              and workshops across the Campus.
            </p>
            <Figure name="tools/tools-1.png" />
          </div>
          <div class="tool">
            <p>
              <b>Compass</b> is an iOS-based navigational tool for the Campus
              Complex that helps learners orient and find nearby learning
              environments.
            </p>
            <video muted autoplay playsinline loop>
              <source src="figures/tools/compass.mov" />
            </video>
          </div>
          <!-- <Figure name="tools/compass.mov" /> -->
          <div class="tool">
            <p>
              <b>Explorer Guide</b> is a feature of the Campus Concept Trust
              that gives fellows all the information they need to successfully
              navigate the program.
            </p>
            <Figure name="tools/guide.png" />
          </div>

          <div class="tool">
            <p>
              <b
                ><a
                  href="https://www.are.na/teal/exploration-charting"
                  target="_blank"
                  >Exploration Charts</a
                ></b
              >
              are an analog research tool for learners to log the provenance or
              history of a particular concept they’re exploring. It helps
              provide structure to open-ended learning experiences and makes it
              easy to share a learning journey with others.
            </p>
            <Figure name="tools/exploration-chart.png" />
          </div>
          <div class="tool">
            <p>
              <b>Campus in a Box</b> is a kit for Campus Tours that materializes
              the values and principles of the Campus when it pops up in a new
              space.
            </p>
            <Figure name="tools/tools-2.png" />
          </div>
          <div class="tool">
            <p>
              <b>Landmarks</b> were gifted to each learning environment in the
              Campus. They were a wooden block carved in the shape of Rounner,
              the Campus Complex logo. Landmarks create an encouraging sense of
              aesthetic continuity across the Campus but also allows each space
              to display the Campus energy in their unique style.
            </p>
            <Figure name="tools/tools-3.png" />
          </div>
          <div class="tool">
            <p>
              <b>Orientation</b> is a process for introducing the fellows and
              spaces to one another. Fellows learn about the steward’s
              educational journey, the story behind the space, and the tools
              that are available on-site.
            </p>
            <Figure name="tools/tools-4.png" />
          </div>

          <!-- <Header first="Tools for Campus Building" /> -->
          <div class="tool">
            <p>
              <b>Agreements</b> are pamphlets about the Campus Complex program,
              hand-delivered to prospective fellows and spaces. They outline the
              structure of the program and invite people to participate in its
              production.
            </p>
            <Figure name="tools/tools-5.png" />
          </div>
          <div class="tool">
            <p>
              <b
                ><a href="https://concept-trust.glitch.me/" target="_blank"
                  >Concept Trust</a
                ></b
              >
              is custom-tailored software for the Campus Complex that supports
              the creative explorations of learners by keeping a record of their
              ideas and references.
            </p>
          </div>
          <div class="tool">
            <p>
              <b>Visiting Hours</b> is a structure that allows for freedom of
              movement across the Campus Complex. During these learning
              sessions, stewards support the fellows in their
              explorations—helping them learn how to use tools, think through
              project ideas, and figure out what to do next.
            </p>
            <Figure name="tools/visiting-hours.png" />
          </div>
          <div class="tool">
            <p>
              <b>Workshops</b> are dedicated times for an Educator to share a
              skill or craft with the group, often creating a shareable artifact
              that participants can take home with them for future reference.
            </p>
            <Figure name="tools/workshop.png" />
          </div>

          <!-- <Header first="Tools for Timekeeping" /> -->

          <div class="tool">
            <p>
              <b>Voicemail Box</b> is an audio time capsule for the Campus.
              Fellows, peers, space stewards, and friends of the Campus can call
              a phone number and leave a message for the future. Try it out!
            </p>
            <Figure name="tools/tools-7.png" />
          </div>
          <div class="tool">
            <p>
              <b>Peer-Educator Conference</b> is the concluding event of the
              Campus, like a Parent-Teacher Conference but for fellows’ peers.
              The fellows share what they’ve been exploring and the peers help
              to envision what types of spaces can be added to the Campus in the
              future.
            </p>
            <Figure name="tools/conference.png" />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  </Page>
</template>

<script>
import Page from "@/components/Page.vue";
import Title from "@/components/Title.vue";
import Footer from "@/components/Footer.vue";
// import Header from "@/components/Header.vue";
import Figure from "@/components/Figure.vue";
import Shape from "@/components/Shape.vue";

export default {
  name: "Placemaking",
  components: {
    Page,
    Title,
    Footer,
    Figure,
    Shape,
  },
  data() {
    return {};
  },
  computed: {},
  methods: {},
};
</script>

<style>
.tool {
  padding: 2.4rem;
  border-radius: 0.8rem;
  background: var(--tan-02);
  margin: 3.2rem;
  margin-left: 0;
  margin-right: 0;
  position: relative;
}

.tool p {
  margin-top: 0;
  margin-left: 2.4rem;
}

.tool p b {
  display: block;
  margin-left: -2.4rem;
}

.tool span {
  position: absolute;
  transform: translate(-6.4rem);
}
</style>
